import React from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';

const BookingConfirmation = ({ bookingData, onNewBooking }) => {
  const bookingReference = bookingData.bookingReference || `DA${Date.now()?.toString()?.slice(-6)}`;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDownloadConfirmation = async () => {
    const element = document.getElementById('booking-summary-card');
    if (!element) return;

    try {
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Booking-${bookingReference}.pdf`);
    } catch (error) {
      console.error('PDF Download Error:', error);
      // Fallback
      alert('Could not generate PDF. Please try printing the page instead.');
    }
  };

  const handleAddToCalendar = () => {
    const startDate = new Date(bookingData.checkIn)?.toISOString()?.replace(/[-:]/g, '')?.split('.')?.[0] + 'Z';
    const endDate = new Date(bookingData.checkOut)?.toISOString()?.replace(/[-:]/g, '')?.split('.')?.[0] + 'Z';

    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=Dandeli Adventure Resort Stay&dates=${startDate}/${endDate}&details=Booking Reference: ${bookingReference}&location=Dandeli Adventure Resorts, Karnataka, India`;

    window.open(calendarUrl, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="text-center py-8">
        <div className="w-20 h-20 bg-success rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={40} className="text-success-foreground" />
        </div>
        <h2 className="text-2xl font-heading font-bold text-card-foreground mb-2">
          Booking Confirmed!
        </h2>
        <p className="text-muted-foreground">
          Your reservation has been successfully processed
        </p>
      </div>

      {/* Booking Details Card (ID added for PDF capture) */}
      <div id="booking-summary-card" className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-heading font-semibold text-card-foreground">
            Booking Details
          </h3>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Booking Reference</p>
            <p className="font-mono font-bold text-primary text-lg">{bookingReference}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Icon name="User" size={16} className="text-primary mt-1" />
              <div>
                <p className="font-medium text-card-foreground">Guest Name</p>
                <p className="text-muted-foreground">
                  {bookingData?.title} {bookingData?.firstName} {bookingData?.lastName}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Icon name="Mail" size={16} className="text-primary mt-1" />
              <div>
                <p className="font-medium text-card-foreground">Email</p>
                <p className="text-muted-foreground">{bookingData?.email}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Icon name="Phone" size={16} className="text-primary mt-1" />
              <div>
                <p className="font-medium text-card-foreground">Phone</p>
                <p className="text-muted-foreground">{bookingData?.phone}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Icon name="Bed" size={16} className="text-primary mt-1" />
              <div>
                <p className="font-medium text-card-foreground">Accommodation</p>
                <p className="text-muted-foreground">
                  {bookingData?.roomType?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Icon name="Calendar" size={16} className="text-primary mt-1" />
              <div>
                <p className="font-medium text-card-foreground">Check-in</p>
                <p className="text-muted-foreground">{formatDate(bookingData?.checkIn)}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Icon name="Calendar" size={16} className="text-primary mt-1" />
              <div>
                <p className="font-medium text-card-foreground">Check-out</p>
                <p className="text-muted-foreground">{formatDate(bookingData?.checkOut)}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Icon name="Users" size={16} className="text-primary mt-1" />
              <div>
                <p className="font-medium text-card-foreground">Guests</p>
                <p className="text-muted-foreground">{bookingData?.guests} guest{bookingData?.guests > 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>
        </div>

        {bookingData?.specialRequests && (
          <div className="mt-6 pt-4 border-t border-border">
            <div className="flex items-start space-x-3">
              <Icon name="MessageSquare" size={16} className="text-primary mt-1" />
              <div>
                <p className="font-medium text-card-foreground">Special Requests</p>
                <p className="text-muted-foreground">{bookingData?.specialRequests}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Important Information */}
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
        <h4 className="font-medium text-card-foreground mb-3 flex items-center">
          <Icon name="Info" size={16} className="text-accent mr-2" />
          Important Information
        </h4>
        <ul className="text-sm text-muted-foreground space-y-2">
          <li>• Check-in time: 2:00 PM | Check-out time: 11:00 AM</li>
          <li>• Please carry a valid government-issued photo ID</li>
          <li>• Confirmation email has been sent to {bookingData?.email}</li>
          <li>• For any changes or queries, contact us at +91 98765 43210</li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Button
          variant="outline"
          onClick={handleDownloadConfirmation}
          iconName="Download"
          iconPosition="left"
          fullWidth
        >
          Download PDF
        </Button>

        <Button
          variant="outline"
          onClick={handleAddToCalendar}
          iconName="Calendar"
          iconPosition="left"
          fullWidth
        >
          Add to Calendar
        </Button>

        <Button
          variant="default"
          onClick={onNewBooking}
          iconName="Plus"
          iconPosition="left"
          fullWidth
        >
          New Booking
        </Button>
      </div>

      {/* Contact Support */}
      <div className="text-center py-6 border-t border-border">
        <p className="text-muted-foreground mb-4">Need help with your booking?</p>
        <div className="flex justify-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            iconName="Phone"
            iconPosition="left"
            onClick={() => window.location.href = 'tel:+919876543210'}
          >
            Call Us
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="MessageCircle"
            iconPosition="left"
            onClick={() => window.open('https://wa.me/919876543210', '_blank')}
          >
            WhatsApp
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="Mail"
            iconPosition="left"
            onClick={() => window.location.href = 'mailto:dandeliadventure.info@gmail.com'}
          >
            Email
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;